import * as React from "react";
import Modul from 'diy/module/Modul';
import SyncPosition, {PositionBox} from "diy/components/SyncPosition";
import {OpacityAnimation} from "../../../overlay/components/Animation";
import BackgroundSelectorView, {Tab} from './common/BackgroundSelectorView';
import {ColorType} from './common/ColorSelectorView';
import {BackgroundSettings} from "../state";
import {ConfigurableGroup} from "../../sectionContainer/index";
import {Dropdown} from './toolbar/Dropdown';

import {autoprefixer} from "diy/material-ui/autoprefixer";

import ImageIcon from 'material-ui/svg-icons/image/image';
import DeleteIcon from 'material-ui/svg-icons/action/delete';
import l10n = require('diy/util/l10n');

import FlatButton from "material-ui/FlatButton";

export interface GroupOverlayProps extends React.Props<GroupOverlay> {
    moduleId?: number;
    backgroundSettings?: BackgroundSettings;
    configurableGroup: ConfigurableGroup;
    onColorClicked?: (backgroundColorType: ColorType, value?: string) => void;
    onTabClicked?: (tab: Tab) => void;
    onManageGroupBackgroundOpened?: () => void;
    onManageGroupBackgroundClosed?: () => void;
    showDeleteButton: boolean;
    backgroundMenuOpen: boolean;

    onChangeBackgroundImage?(
        imageId: number,
        imageType: string,
        successCallback: () => void,
        errorCallback: () => void
    ): void;
    onChangeBackground?(background: BackgroundSettings): void;
    onDelete(moduleId: number): void;
    onEditImageClicked(): void;
    onSelectImageClicked(): void;
    onApplyOnAllGroups?(useBackgroundFromModule: number): void;
}

export default class GroupOverlay extends React.Component<GroupOverlayProps, {}> {
    static ZINDEX = 113;

    private deleteDropDownRef: Dropdown;
    getModule() {
        return Modul.getInstance(this.props.moduleId);
    }

    getModuleEl(): JQuery {
        if (this.getModule()) {
            return this.getModule().modul;
        }
        return null;
    }

    render() {
        if (!this.props.moduleId) {
            return null;
        }

        return (
            <SyncPosition element={this.getModuleEl()[0]} key="overlaySync">
                {
                    (box: PositionBox) => (
                        <OpacityAnimation duration={0.1} key="overlayAnimation">
                        {this.renderOverlay(box)}
                        </OpacityAnimation>
                    )
                }
            </SyncPosition>
        );
    }

    renderOverlay(box: PositionBox) {
        let styles = this.getStyles(box);

        let configurable = this.props.configurableGroup;
        let onlyColor = configurable.color && !configurable.image && !configurable.video;

        return (
            <div className="wrapper" style={styles.root}>
                <div style={styles.border}>
                    {(configurable.color || configurable.image || configurable.theming || configurable.video) ?
                    <div onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}
                        onTouchTap={e => e.stopPropagation()}
                        style={styles.menuWrapper}
                    >
                        <Dropdown
                            dataTestId={`section-group-overlay-${this.props.moduleId}`}
                            Icon={<ImageIcon/>}
                            open={this.props.backgroundMenuOpen}
                            buttonStyle={styles.menuButton}
                            layerStyle={{
                                marginTop: 0,
                                padding: 0,
                                minWidth: 240
                            }}
                            horizontalAlign="left"
                            onOpen={this.props.onManageGroupBackgroundOpened}
                            onClose={this.props.onManageGroupBackgroundClosed}
                        >
                            <BackgroundSelectorView
                                onlyColor={onlyColor}
                                onChange={this.props.onChangeBackground}
                                onImageChange={this.props.onChangeBackgroundImage}
                                onEditImageClicked={this.props.onEditImageClicked}
                                onSelectImageClicked={this.props.onSelectImageClicked}
                                background={this.props.backgroundSettings}
                                onColorClicked={this.props.onColorClicked}
                                onTabClicked={this.props.onTabClicked}
                                parallaxAvailable={false}
                                onApplyOnAllGroups={
                                    this.props.onApplyOnAllGroups ?
                                    () => {this.props.onApplyOnAllGroups(this.props.moduleId); }
                                    : undefined
                                }
                            />
                        </Dropdown>
                    </div>
                    : null}
                    {this.props.showDeleteButton ?
                    <div onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}
                        onTouchTap={e => e.stopPropagation()}
                        style={styles.deleteWrapper}
                    >
                        <Dropdown
                            dataTestId={`delete-group-${this.props.moduleId}`}
                            Icon={<DeleteIcon/>}
                            buttonStyle={styles.menuButton}
                            layerStyle={{
                                marginTop: -36,
                                padding: 10,
                                maxWidth: 220
                            }}
                            horizontalAlign="left"
                            ref={(ref: Dropdown) => this.deleteDropDownRef = ref}
                        >
                            <div style={autoprefixer({
                                display: "block"
                            })}>
                                <p style={{fontSize: 15}}>
                                    {l10n.getString('section.actionBar.buttons.delGroup.label')}
                                </p>
                                <p style={{textAlign: "right", paddingTop: 10}}>
                                    <FlatButton
                                        label={l10n.getString('section.actionBar.buttons.delGroup.no')}
                                        secondary={true}
                                        style={styles.flatButton}
                                        onTouchTap={() => { this.deleteDropDownRef.setState({open: false}); }}
                                    />
                                    <FlatButton
                                        label={l10n.getString('section.actionBar.buttons.delGroup.yes')}
                                        style={styles.flatButton}
                                        primary={true}
                                        onTouchTap={() => { this.props.onDelete(this.props.moduleId); }}
                                    />
                                </p>
                            </div>
                        </Dropdown>
                        </div>
                        : null }
                </div>
            </div>
        );
    }

    private getStyles(box: PositionBox) {
        return {
            root: {
                position: "absolute",
                zIndex: GroupOverlay.ZINDEX,
                left: box.left,
                top: box.top,
                width: box.width,
                height: box.height,
                boxSizing: 'border-box',
                border: '2px solid rgba(33, 150, 243, 1)'
            },
            border: {
                position: "absolute",
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                background: "-moz-linear-gradient(top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 150px); " +
                    "background: -webkit-linear-gradient(top, rgba(0,0,0,0.6) 0%,rgba(0,0,0,0) 150px); " +
                    "background: linear-gradient(to bottom, rgba(0,0,0,0.6) 0%,rgba(0,0,0,0) 150px)"
            },
            menuWrapper: autoprefixer({
                float: 'right',
                width: '36px',
                height: '36px',
                background: 'rgba(236, 238, 241, 0.8)',
                alignItems: 'center',
                display: 'flex',
            }),
            menuButton: {
                left: '-6px',
                width: 36,
                height: 36,
                border: 0,
                padding: 0,
                marginLeft: 6
            },
            deleteWrapper: autoprefixer({
                float: 'left',
                width: '36px',
                height: '36px',
                background: 'rgba(236, 238, 241, 0.8)',
                alignItems: 'flex-start',
                display: 'flex'
            }),
            flatButton: {
                fontSize: 12
            }
        };
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/module/section/components/GroupOverlay.tsx
 **/